import React, {
  FC,
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';

import FilterOptions from 'organisms/Filter/components/FilterOptions';
import DatePickerFilter from 'organisms/Filter/forms/DatePickerFilter';
import MonthPickerFilter from 'organisms/Filter/forms/MonthPickerFilter';
import ListFilter from 'organisms/Filter/forms/ListFilter';
import { Popover, IconButton, Stack, Box, Tooltip } from '@mui/material';

import { neutrals } from 'utils/styles/color';
import {
  IFilterContext,
  IFilterOption,
  IFilterProps,
} from 'organisms/Filter/types';

import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
// import loaderGif from 'assets/images/loaderGif.gif';

export const FilterContext = createContext<IFilterContext>({
  filter: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFilter: (c) => c,
  options: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setOptions: (o) => o,
});

const Filter: FC<IFilterProps> = ({
  options,
  filterExtend,
  onChange,
  loading,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterOptions, setFilterOptions] = useState<IFilterOption[]>([]);
  const [filter, setFilter] = useState({});

  const boxRef = useRef(null);

  const onFilterChange = useCallback(
    (nextFilter: { [key: string]: unknown }) => {
      setFilter((prev) => {
        onChange({ ...prev, ...nextFilter });
        return { ...prev, ...nextFilter };
      });
    },
    []
  );

  useEffect(() => {
    setFilterOptions(options);
  }, [options]);

  useEffect(() => {
    if (!filterExtend) return;
    onFilterChange(filterExtend);
  }, [filterExtend]);

  useEffect(() => {
    onChange(filter);
  }, [filter]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    document.body.setAttribute('id', 'body');
  };

  const handleReset = () => {
    setFilter({});
  };

  const handleClose = () => {
    setAnchorEl(null);
    document.body.setAttribute('id', '');
  };

  const isFilled = (() => {
    if (!filter) return false;
    return !Object.values(filter).every((value) => {
      if (Array.isArray(value) && value.length === 0) return true;
      if (typeof value === 'string' && value === '') return true;
      return value === undefined;
    });
  })();

  const open = Boolean(anchorEl);
  const id = open ? 'filter-popover' : undefined;
  const activeOption = filterOptions.find((o) => o.active);

  // eslint-disable-next-line
  console.log(filter, loading);

  return (
    <FilterContext.Provider
      value={{
        filter,
        setFilter: onFilterChange,
        options: filterOptions,
        setOptions: setFilterOptions,
      }}
    >
      <div>
        <IconButton
          sx={{ p: 0 }}
          onClick={handleClick}
          aria-describedby={id}
          color="primary"
          aria-label="Filter data"
          component="span"
        >
          <FilterIcon />
        </IconButton>
        {isFilled && (
          <Tooltip title="Reset all applied filters">
            <IconButton
              sx={{ p: 0, ml: 1 }}
              onClick={handleReset}
              color="primary"
              aria-label="Reset filter"
              component="span"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          container={boxRef.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          sx={{
            position: 'unset',
          }}
          PaperProps={{
            sx: {
              overflowY: 'hidden',
              minWidth: '600px !important',
              width: 600,
              height: '362px',
              minHeight: '362px',
            },
          }}
        >
          <Stack direction="row" sx={{ height: 362, position: 'relative' }}>
            {/* {loading ? (
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'rgba(255, 255, 255, .7)',
                  zIndex: 9,
                }}
              >
                <img src={loaderGif} alt="Data loading" />
              </Box>
            ) : null} */}
            <Box
              sx={{
                width: 184,
                borderRight: `1px solid ${neutrals[100]}`,
              }}
            >
              <FilterOptions />
            </Box>
            {/* , opacity: loading ? 0 : 1 */}
            <Box sx={{ width: 416 }}>
              {activeOption?.code === 'invoice_date' && (
                <DatePickerFilter name="invoice_date" />
              )}
              {activeOption?.code === 'completion_date' && (
                <DatePickerFilter name="completion_date" />
              )}
              {activeOption?.code === 'sent_date' && (
                <DatePickerFilter name="sent_date" />
              )}
              {activeOption?.code === 'created_at' && (
                <DatePickerFilter name="created_at" />
              )}
              {activeOption?.code === 'date' && (
                <MonthPickerFilter name="date" />
              )}
              {activeOption?.code === 'google_invoice_date' && (
                <MonthPickerFilter name="google_invoice_date" />
              )}
              {activeOption?.code === 'invoice_automation' && (
                <ListFilter name="invoice_automation" />
              )}
              {activeOption?.code === 'payment_schedule' && (
                <ListFilter name="payment_schedule" />
              )}
              {activeOption?.code === 'invoice_creation_approval' && (
                <ListFilter name="invoice_creation_approval" />
              )}
              {activeOption?.code === 'contract_type' && (
                <ListFilter name="contract_type" />
              )}
              {activeOption?.code === 'status' && <ListFilter name="status" />}
              {activeOption?.code === 'sales_rep' && (
                <ListFilter name="sales_rep" />
              )}
              {activeOption?.code === 'service_type' && (
                <ListFilter name="service_type" />
              )}
              {activeOption?.code === 'company' && (
                <ListFilter name="company" />
              )}
              {activeOption?.code === 'accountant' && (
                <ListFilter name="accountant" />
              )}
              {activeOption?.code === 'invoice_status' && (
                <ListFilter name="invoice_status" />
              )}
              {activeOption?.code === 'subject' && (
                <ListFilter name="subject" />
              )}
              {activeOption?.code === 'close_date' && (
                <DatePickerFilter name="close_date" />
              )}
              {activeOption?.code === 'group' && <ListFilter name="group" />}
              {activeOption?.code === 'integrated' && (
                <ListFilter name="integrated" />
              )}
              {activeOption?.code === 'invoice_creation_automation' && (
                <ListFilter name="invoice_creation_automation" />
              )}
              {activeOption?.code === 'billing_data_check' && (
                <ListFilter name="billing_data_check" />
              )}
              {activeOption?.code === 'google_sales_rep_name' && (
                <ListFilter name="google_sales_rep_name" />
              )}
              {activeOption?.code === 'googler_name' && (
                <ListFilter name="googler_name" />
              )}
              {activeOption?.code === 'google_sales_rep_email' && (
                <ListFilter name="google_sales_rep_email" />
              )}
              {activeOption?.code === 'action' && <ListFilter name="action" />}
              {activeOption?.code === 'target_type' && (
                <ListFilter name="target_type" />
              )}
              {activeOption?.code === 'user_id' && (
                <ListFilter name="user_id" />
              )}
            </Box>
          </Stack>
        </Popover>

        {/* Container for menu paper */}
        <Box
          className="filter-popover-container"
          sx={{
            display: open ? 'block' : 'none',
            width: '654px',
            height: '393px',
            position: 'absolute',
            top: '40px',
            left: '5px',
          }}
        >
          <Box
            ref={boxRef}
            sx={{
              zIndex: 10,
              position: 'relative',
              width: '100%',
              height: '100%',
              '.MuiPaper-root': {
                top: '10px !important',
                left: `14px !important`,
              },
            }}
          />
        </Box>
      </div>
    </FilterContext.Provider>
  );
};

export default Filter;
